import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Pagination,
  Stack,
  styled,
  Grid,
  Paper,
  Button,
  Avatar,
  Rating,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "axios";
import homebanner from "@assets/images/istockphoto-3.jpg";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import SEO from "../../../utils/SeoComponent";

// ========== STYLED COMPONENTS ========== //
const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${homebanner})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  position: "relative",
  height: "600px",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "150px",
    background: "linear-gradient(to top, #fff, transparent)",
  },
}));

const SearchBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 150,
  left: 0,
  right: 0,
  textAlign: "center",
  zIndex: 2,
  [theme.breakpoints.down("md")]: {
    bottom: 40,
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  height: "100%",
  boxShadow: "none",
  border: "1px solid #E2E8F0",
  borderRadius: theme.spacing(1),
}));

const TestimonialHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: "#0F172A",
  marginBottom: theme.spacing(2),
}));

const StyledRating = styled(Rating)({
  color: "#FFA723",
  marginBottom: 16,
});

// ========== MAIN COMPONENT ========== //
const TestimonialsPage = () => {
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query") || "";
  const [currentPage, setCurrentPage] = useState(1);
  const [reviews, setReviews] = useState([]);
  const itemsPerPage = 6;
  const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;

  // Fetch all areas on component mount
  useEffect(() => {
    const fetchAreas = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${DESCO_API_BASE}/review/index`);

        setReviews(response.data?.data?.reviews);
      } catch (error) {
        console.error("Error fetching areas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAreas();
  }, []);

  // Determine which data set to use for display
  const displayData = reviews;

  // Calculate pagination
  const pageCount = Math.ceil((displayData?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAreas = Array.isArray(displayData)
    ? displayData.slice(startIndex, startIndex + itemsPerPage)
    : [];

  // Handle page change
  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <SEO slug={"testimonials"} />
      <Box sx={{ overflowX: "hidden" }}>
        {/* Hero Banner */}
        <HeroSection>
          {/* <img src={homebanner} alt="Dubai Areas" /> */}
          <SearchBox>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 3,
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                }}
              >
                Why Our Clients Trust Us
              </Typography>
              <Typography
                variant="h5"
                component="p"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: 3,
                  textShadow: "0 2px 8px rgba(0,0,0,0.5)",
                }}
              >
                Discover what our customers are saying about their experiences.
              </Typography>
            </motion.div>
          </SearchBox>
        </HeroSection>

        {/* Areas Grid */}
        <Container sx={{ py: 8 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 6,
              textAlign: "center",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -10,
                left: "50%",
                transform: "translateX(-50%)",
                width: 80,
                height: 4,
                backgroundColor: "#C10E14",
              },
            }}
          >
            All Reviews
          </Typography>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography>Loading Reviews...</Typography>
            </Box>
          ) : paginatedAreas.length > 0 ? (
            <Grid container spacing={3}>
              {paginatedAreas?.map((testimonial) => (
                <Grid item xs={12} md={4} key={testimonial.id}>
                  <TestimonialCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: "#C10E14",
                            width: 48,
                            height: 48,
                            mr: 2,
                          }}
                          src={`https://project.trogon.info/desco/${testimonial?.avatar}`}
                        />

                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(testimonial?.created_at).fromNow()}
                          </Typography>
                        </Box>
                      </Box>

                      <StyledRating value={testimonial.rating} readOnly />

                      <TestimonialHeading variant="h6">
                        {testimonial.title}
                      </TestimonialHeading>

                      <Typography variant="body2" color="text.secondary">
                        {testimonial.content}
                      </Typography>
                    </CardContent>
                  </TestimonialCard>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6">No reviews found.</Typography>
            </Box>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <Stack spacing={2} sx={{ mt: 6, alignItems: "center" }}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontWeight: 600,
                    "&.Mui-selected": {
                      backgroundColor: "#C10E14",
                      color: "white",
                    },
                  },
                }}
              />
            </Stack>
          )}
        </Container>
      </Box>
    </>
  );
};

export default TestimonialsPage;
